import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold ' +
    'transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40 ' +
    'focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ' +
    'disabled:pointer-events-none disabled:opacity-50 ' +
    '[&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:     'bg-coral text-white shadow-cta hover:bg-coral-600',
        destructive: 'bg-rose-500 text-white shadow-cta hover:bg-rose-600',
        outline:     'bg-white dark:bg-white/10 text-ink dark:text-white shadow-pill dark:shadow-none ring-1 ring-ink/10 dark:ring-white/15 hover:shadow-pill-active dark:hover:bg-white/15',
        secondary:   'bg-white/72 dark:bg-white/10 backdrop-blur-md text-ink dark:text-white shadow-pill dark:shadow-none hover:bg-white dark:hover:bg-white/15',
        ghost:       'text-ink dark:text-white hover:bg-white/60 dark:hover:bg-white/10',
        link:        'text-coral underline-offset-4 hover:underline',
        soft:        'bg-pearl-100 dark:bg-white/[0.06] text-ink dark:text-white hover:bg-pearl-200 dark:hover:bg-white/10',
        glass:       'bg-white/55 dark:bg-white/[0.06] backdrop-blur-md text-ink dark:text-white ring-1 ring-ink/[0.08] dark:ring-white/10 hover:bg-white/70 dark:hover:bg-white/10',
        dark:        'bg-ink-900 text-white shadow-cta-dark hover:bg-ink-800',
      },
      size: {
        default: 'h-10 px-5 text-[13px] rounded-full',
        sm:      'h-8  px-3.5 text-[12px] rounded-full',
        lg:      'h-12 px-6 text-[14px] rounded-full',
        icon:    'h-10 w-10 rounded-full',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
